export interface HealthResponse {
  status: string;
}

/** Reads the API process health through the dev and preview proxy. */
export async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch('/healthz');
  if (!res.ok) throw new Error(`healthz returned ${res.status}`);
  return (await res.json()) as HealthResponse;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

/**
 * The access token lives in memory only. A reload loses it and the HttpOnly refresh cookie
 * brings it back, so nothing a script can read persists.
 */
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onAuthLost(handler: (() => void) | null): void {
  onSessionLost = handler;
}

async function toError(res: Response): Promise<ApiError> {
  let envelope: ErrorEnvelope = {};
  try {
    envelope = (await res.json()) as ErrorEnvelope;
  } catch {
    // not JSON
  }
  return new ApiError(
    res.status,
    envelope.error?.code ?? 'UNKNOWN_ERROR',
    envelope.error?.message ?? `Request failed with status ${String(res.status)}`,
    envelope.error?.details,
  );
}

/**
 * Exchanges the refresh cookie for a new access token. Concurrent callers share one
 * request, so several failing calls never rotate the token more than once.
 */
export function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        accessToken = null;
        return false;
      }
      const body = (await res.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Send the access token and refresh once on 401. Default true. */
  auth?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = options;
  const send = (): Promise<Response> =>
    fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(auth && accessToken !== null ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

  let res = await send();
  if (res.status === 401 && auth) {
    if (await refreshAccessToken()) {
      res = await send();
    } else {
      onSessionLost?.();
    }
  }
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The words for a failed deal action: the one cause a broker can fix, else the general line. */
export function dealErrorText(e: unknown, t: (key: string) => string): string {
  return e instanceof ApiError && e.code === 'AGENCY_PHONE_MISSING'
    ? t('deal.agencyPhoneMissing')
    : t('deal.generic');
}
